/* Gleva · Direction B · gvb-cart.js — the bag: Impact's <cart-drawer> and the /cart page (BUILD-SPEC §2.4).
   Loaded by sections/cart-drawer.liquid and sections/main-cart.liquid (ES module; runs once).
   Markup: snippets/gvb-cart.liquid (+ gvb-cart-line). Everything is server-rendered; this file only:
   - changes quantities: one line, or a whole kit group (every line of one Kaching bundle × the new kit count),
     through /cart/update.js with Impact's bundled sections, then dispatches Impact's `cart:change`, so Impact's
     drawer re-renders itself, <cart-count> and gvb.js's badge update, and PDP sections hear it;
   - removes a line or a kit; adds the cross-sell / empty-bag bestseller (/cart/add.js, same bundling; no toast:
     the shopper is already in the bag);
   - toggles "Add a note" (the textarea is inside Impact's <cart-note>, which saves it on change);
   - keeps focus, the open note, open "What's in the kit" lists and the scroll position across every re-render
     (Impact replaces the drawer's children on each cart:change / cart:refresh) and fills the bag's live region;
   - re-renders the /cart page section after any cart change (it is not one of Impact's bundled sections);
   - guards Impact's drawer against a cart:change without its bundled section (it would throw; foundation NOTES §8).
   Launch sprint (byob-diwali/CART-SPEC.md, prelaunch/tracking/REPORT.md):
   - decreases and removals go through /cart/change.js (one call per line), so Shopify publishes
     product_removed_from_cart (GA4 remove_from_cart, Snap REMOVE_FROM_CART); increases stay on /cart/update.js (F1);
   - after a JSON add, Shiprocket analytics gets its "atc" event when window.SHIPROCKET_ANALYTICS exists (F2, guarded);
   - free gift: "Pick" removes the old gift line (change.js) and adds the chosen one with property _gvb_gift (the price
     comes from the merchant's automatic discount, never from here); "Change gift" opens the row; "Pick your gift" scrolls;
   - BYOB "Edit box": removes the box line (change.js), then opens the box page with the same picks (?box=&pick=);
   - studio backdrop: the bag's thumbnails are classified by the site-wide assets/gvb-studio.js (it watches the DOM,
     so Impact's re-renders are picked up and cached results apply before paint); this file only asks it to rescan.
   Prices are server-rendered with gvb-price; this file never formats money. */
import { G } from 'gvb';

const $ = (s, r) => (r || document).querySelector(s);
const $$ = (s, r) => Array.from((r || document).querySelectorAll(s));
const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const pageBag = () => $('[data-gvb-bag][data-context="page"]');
/* an alternate cart template (?view=…, QA copies) must re-render from itself: never bundle the page section then */
const pageView = () => { try { return new URLSearchParams(location.search).get('view') || ''; } catch (e) { return ''; } };
const esc = (s) => (window.CSS && CSS.escape ? CSS.escape(s) : String(s).replace(/["\\]/g, '\\$&'));
const sectionIdOf = (el) => {
  const s = el && el.closest('.shopify-section');
  return s ? s.id.replace('shopify-section-', '') : '';
};

/* ---------- what to put back after the next re-render ---------- */
let pending = null;
let clearT = 0;
const focusKey = (el) => {
  if (!el || !el.closest || !el.closest('[data-gvb-bag]')) return null;
  const line = el.closest('[data-gvb-line]');
  if (line) {
    const what = el.matches('[data-qty="1"]') ? 'inc' : el.matches('[data-qty="-1"]') ? 'dec'
      : el.matches('[data-remove]') ? 'remove' : el.closest('summary') ? 'summary' : 'name';
    return { line: line.dataset.focus, what };
  }
  if (el.matches('[data-gvb-note]')) return { sel: '[data-gvb-note]' };
  if (el.matches('textarea[name="note"]')) return { sel: 'textarea[name="note"]' };
  return null;
};
const snapshot = () => {
  const s = { kits: [], scroll: {}, note: {}, ctx: null, focus: null };
  $$('[data-gvb-bag]').forEach((b) => {
    const ctx = b.dataset.context;
    const sc = $('[data-bag-scroll]', b);
    const dlg = b.closest('cart-drawer');
    /* keep the scroll position only for a bag that is on screen, and only within the same state (items/empty) */
    if (sc && (!dlg || dlg.hasAttribute('open'))) s.scroll[ctx] = { top: sc.scrollTop, state: b.dataset.state };
    $$('details[data-kit-list][open]', b).forEach((d) => s.kits.push(d.dataset.kitList));
    const nf = $('.c-note-field', b), ta = nf && $('textarea', nf);
    if (nf) s.note[ctx] = { open: !nf.hidden, value: ta ? ta.value : '', focus: !!ta && document.activeElement === ta, sel: ta ? [ta.selectionStart, ta.selectionEnd] : null };
    if (b.contains(document.activeElement)) { s.ctx = ctx; s.focus = focusKey(document.activeElement); }
  });
  return s;
};
const hold = (meta) => {
  pending = Object.assign(snapshot(), meta || {});
  clearTimeout(clearT);
  clearT = setTimeout(() => { pending = null; }, 2500);
};
const restore = (bag) => {
  const s = pending;
  if (!s || !bag) return;
  const ctx = bag.dataset.context;
  s.kits.forEach((k) => { const d = $(`details[data-kit-list="${esc(k)}"]`, bag); if (d) d.open = true; });
  const n = s.note[ctx], nf = $('.c-note-field', bag);
  if (n && nf) {
    nf.hidden = !n.open;
    const nb = $('[data-gvb-note]', bag);
    if (nb) nb.setAttribute('aria-expanded', n.open ? 'true' : 'false');
    const ta = $('textarea', nf);
    if (ta && ta.value !== n.value) ta.value = n.value;
    if (ta && n.focus && !s.focus) { ta.focus({ preventScroll: true }); if (n.sel) { try { ta.setSelectionRange(n.sel[0], n.sel[1]); } catch (e) { /* ignore */ } } }
  }
  const sc = $('[data-bag-scroll]', bag);
  const sp = s.scroll[ctx];
  if (sc && sp && sp.state === bag.dataset.state) sc.scrollTop = sp.top;
  if (s.ctx !== ctx) return;
  let t = null;
  const f = s.focusTo || s.focus;
  if (f && f.line) {
    const li = $(`[data-gvb-line][data-focus="${esc(f.line)}"]`, bag);
    if (li) {
      t = f.what === 'inc' ? $('[data-qty="1"]', li) : f.what === 'dec' ? $('[data-qty="-1"]', li)
        : f.what === 'summary' ? $('summary', li) : f.what === 'remove' ? $('[data-remove]', li) : $('.c-name a', li);
      if (t && t.disabled) t = $('[data-qty="-1"]', li) || $('.c-name a', li);
    }
  } else if (f && f.sel) {
    t = $(f.sel, bag);
  }
  if (!t && (f || s.panel)) {
    t = bag.closest('cart-drawer') || $('.c-h', bag);
    if (t && t.classList.contains('c-h')) t.setAttribute('tabindex', '-1');
  }
  if (t && document.activeElement !== t) t.focus({ preventScroll: true });
  if (s.announce) {
    const live = $('[data-bag-live]', bag);
    if (live) setTimeout(() => { live.textContent = s.announce; }, 150);
  }
};

/* a re-render = a new [data-gvb-bag] child (Impact's drawer replaceChildren, or our page swap) */
const watch = (el) => {
  if (!el || el._gvbBagObs) return;
  el._gvbBagLast = $('[data-gvb-bag]', el);
  el._gvbBagObs = new MutationObserver(() => {
    const bag = $('[data-gvb-bag]', el);
    if (bag && bag !== el._gvbBagLast) { el._gvbBagLast = bag; sbScan(bag); restore(bag); }
  });
  el._gvbBagObs.observe(el, { childList: true });
};
const watchAll = () => { watch(document.getElementById('cart-drawer')); watch($('.gvb-cart-page')); };

/* ---------- requests (Impact's bundled-sections contract) ---------- */
const bundled = () => {
  const sections = [];
  document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));
  const p = pageBag();
  if (p && p.dataset.sectionId && !pageView() && sections.indexOf(p.dataset.sectionId) < 0) sections.push(p.dataset.sectionId);
  return sections.slice(0, 5);
};
const post = async (url, body) => {
  const res = await fetch(root() + url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    body: JSON.stringify(body)
  });
  let json = {};
  try { json = await res.json(); } catch (e) { /* empty */ }
  if (!res.ok) throw new Error(json.description || json.message || 'Something went wrong. Please try again.');
  return json;
};
const swapPage = (html) => {
  const cur = pageBag();
  if (!cur || !html) return;
  const fresh = $('[data-gvb-bag]', new DOMParser().parseFromString(html, 'text/html'));
  if (fresh) cur.replaceWith(document.importNode(fresh, true));
};
const refreshPage = async () => {
  const p = pageBag();
  if (!p) return;
  const v = pageView();
  try { swapPage(await (await fetch(`${root()}cart?${v ? 'view=' + encodeURIComponent(v) + '&' : ''}section_id=${encodeURIComponent(p.dataset.sectionId)}`)).text()); } catch (e) { location.reload(); }
};
/* one place that tells everyone the cart changed (Impact drawer, <cart-count>, gvb.js badge, PDP sections) */
const emit = (cart, baseEvent, meta) => {
  hold(meta);
  const p = pageBag();
  if (p) {
    const id = p.dataset.sectionId;
    if (cart.sections && cart.sections[id] && !pageView()) swapPage(cart.sections[id]); else refreshPage();
  }
  document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent, cart, gvb: true } }));
};

/* ---------- quantities: a line, or a kit (every line × the new kit count) ---------- */
const setQty = async (line, bag, k) => {
  const updates = {};
  (line.dataset.keys || '').split(',').filter(Boolean).forEach((p) => {
    const i = p.lastIndexOf(':');
    updates[p.slice(0, i)] = Number(p.slice(i + 1)) * k;
  });
  const name = line.dataset.name || 'Item';
  const err = $('[data-err]', line);
  if (err) { err.hidden = true; err.textContent = ''; }
  line.setAttribute('aria-busy', 'true');
  try {
    let cart;
    if (k < Number(line.dataset.k || 0)) {
      /* decreases / removals go through /cart/change.js: Shopify only publishes product_removed_from_cart
         (GA4 remove_from_cart, Snap REMOVE_FROM_CART) for change.js; update.js decreases are silent
         (prelaunch/tracking/REPORT.md F1). A discount can split one cart line into several keys (Kaching's free unit:
         same variant + properties, ₹0 part); changing one split re-merges the rest under the first key. So work per
         variant: bring each variant's total in this group down to its target, re-reading the returned cart each time. */
      const ks = Object.keys(updates);
      const vOf = (key) => key.split(':')[0];
      const want = {};
      ks.forEach((key) => { want[vOf(key)] = (want[vOf(key)] || 0) + updates[key]; });
      const k0 = Number(line.dataset.k || 1);
      let have = (line.dataset.keys || '').split(',').filter(Boolean).map((p) => { const i = p.lastIndexOf(':'); return { key: p.slice(0, i), quantity: Number(p.slice(i + 1)) * k0 }; });
      for (let guard = 0; guard < 8; guard++) {
        let step = null;
        Object.keys(want).some((v) => {
          const rows = have.filter((it) => vOf(it.key) === v && it.quantity > 0);
          const tot = rows.reduce((a, it) => a + it.quantity, 0);
          if (tot <= want[v]) return false;
          const r = rows[rows.length - 1];
          step = { id: r.key, quantity: Math.max(0, r.quantity - (tot - want[v])) };
          return true;
        });
        if (!step) break;
        step.sections = bundled();
        cart = await post('cart/change.js', step);
        have = (cart.items || []).filter((it) => ks.indexOf(it.key) > -1).map((it) => ({ key: it.key, quantity: it.quantity }));
      }
      if (!cart) cart = await cartNow();
    } else {
      cart = await post('cart/update.js', { updates, sections: bundled() });
    }
    const after = cart.items ? cart.items.filter((it) => Object.prototype.hasOwnProperty.call(updates, it.key)) : [];
    const short = k > 0 && after.some((it) => it.quantity < updates[it.key]);
    emit(cart, 'line-item:change', {
      ctx: bag.dataset.context,
      panel: k === 0,
      announce: k === 0 ? `${name} removed from your bag.` : short ? `Only the available quantity of ${name} was kept.` : `${name}, quantity ${k}.`
    });
  } catch (e) {
    line.removeAttribute('aria-busy');
    line._gvbTarget = null;
    const out = $('[data-qty-out]', line);
    if (out) out.textContent = line.dataset.k;
    if (err) { err.textContent = e.message; err.hidden = false; }
  }
};

/* ---------- Shiprocket analytics "atc" for JSON adds (uc.js only hears form submits; REPORT.md F2) ---------- */
const srAtc = (items, cart) => {
  try {
    const sr = window.SHIPROCKET_ANALYTICS;
    if (!sr || typeof sr.event !== 'function' || !items || !items.length) return;
    const it = items[0]; /* uc.js sends one atc per form submit (first line), same as live */
    sr.event('atc', { cart_id: cart && cart.token, name: it.product_title, variant_id: String(it.variant_id),
      product_id: String(it.product_id), qty: String(it.quantity || 1), selling_price: (it.final_price / 100).toFixed(2),
      mrp: ((it.original_price || it.price) / 100).toFixed(2), category: it.product_type || '', image: it.image || '',
      referrer: document.referrer, description: '', event_source: 'theme' });
  } catch (e) { /* analytics must never break an add */ }
};
const cartNow = async () => (await fetch(root() + 'cart.js', { headers: { Accept: 'application/json' } })).json();
const focusOf = (key) => String(key || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

/* ---------- adds from inside the bag (cross-sell, empty-bag bestsellers) ---------- */
const add = async (btn, bag) => {
  if (btn.getAttribute('aria-busy') === 'true') return;
  btn.setAttribute('aria-busy', 'true');
  const name = btn.dataset.name || 'Item';
  try {
    const res = await post('cart/add.js', { items: [{ id: Number(btn.dataset.gvbAdd), quantity: 1 }], sections: bundled() });
    const cart = await cartNow();
    cart.sections = res.sections;
    srAtc(res.items, cart);
    G.flashAdded(btn);
    emit(cart, 'variant:add', { ctx: bag.dataset.context, panel: true, announce: `${name} added to your bag.` });
  } catch (e) {
    btn.removeAttribute('aria-busy');
    const live = $('[data-bag-live]', bag);
    if (live) live.textContent = e.message;
  }
};

/* ---------- free gift: one per order; the theme adds it, Shopify's automatic discount prices it ---------- */
let giftBusy = false;
const pickGift = async (btn, bag) => {
  if (giftBusy) return;
  const id = Number(btn.dataset.gvbGift);
  const name = btn.dataset.name || 'Gift';
  const row = btn.closest('[data-fg]');
  const old = $$('[data-gvb-line][data-gift]', bag);
  if (old.length === 1 && Number(old[0].dataset.gift) === id) { if (row) row.hidden = true; return; }
  giftBusy = true;
  btn.setAttribute('aria-busy', 'true');
  const live = $('[data-bag-live]', bag);
  try {
    for (const l of old) {
      const key = (l.dataset.keys || '').split(',')[0].replace(/:\d+$/, '');
      if (key) await post('cart/change.js', { id: key, quantity: 0 });
    }
    const res = await post('cart/add.js', { items: [{ id, quantity: 1, properties: { _gvb_gift: '1' } }], sections: bundled() });
    const cart = await cartNow();
    cart.sections = res.sections;
    srAtc(res.items, cart);
    const key = res.items && res.items[0] && res.items[0].key;
    emit(cart, 'variant:add', { ctx: bag.dataset.context, panel: true, focusTo: key ? { line: focusOf(key), what: 'name' } : null, announce: `${name} added as your free gift.` });
  } catch (e) {
    btn.removeAttribute('aria-busy');
    if (live) live.textContent = e.message;
  } finally { giftBusy = false; }
};
const openGiftRow = (btn, bag) => {
  const row = $('[data-fg]', bag);
  if (!row) return;
  row.hidden = false;
  btn.setAttribute('aria-expanded', 'true');
  row.scrollIntoView({ block: 'nearest', behavior: G.reduce ? 'auto' : 'smooth' });
  const first = $('.c-fg-tile[aria-pressed="true"]', row) || $('.c-fg-tile', row);
  if (first) first.focus({ preventScroll: true });
};

/* ---------- BYOB "Edit box": the box leaves the bag first (change.js, so the removal is tracked), then its page opens
   with the same picks; the box page pre-fills them from ?box=&pick= ---------- */
const editBox = async (a, bag) => {
  if (a.getAttribute('aria-busy') === 'true') return;
  const line = a.closest('[data-gvb-line]');
  const key = a.dataset.boxKey || (line ? (line.dataset.keys || '').split(',')[0].replace(/:\d+$/, '') : '');
  if (!key) { location.href = a.href; return; }
  a.setAttribute('aria-busy', 'true');
  try {
    await post('cart/change.js', { id: key, quantity: 0 });
    location.href = a.href;
  } catch (e) {
    a.removeAttribute('aria-busy');
    const err = line && $('[data-err]', line);
    if (err) { err.textContent = e.message; err.hidden = false; } else { const live = $('[data-bag-live]', bag); if (live) live.textContent = e.message; }
  }
};

/* ---------- studio backdrop (assets/gvb-studio.js): rescan a fresh bag; gvb-studio.js also observes the DOM itself ---------- */
const sbScan = (scope) => { try { if (window.GVBStudio) window.GVBStudio.scan(scope || document); } catch (e) { /* never block the bag */ } };

/* ---------- note ---------- */
const toggleNote = (btn) => {
  const f = document.getElementById(btn.getAttribute('aria-controls'));
  if (!f) return;
  const open = f.hidden;
  f.hidden = !open;
  btn.setAttribute('aria-expanded', open ? 'true' : 'false');
  if (open) { const ta = $('textarea', f); if (ta) ta.focus(); }
};

/* ---------- events ---------- */
document.addEventListener('click', (e) => {
  const t = e.target.closest && e.target.closest('[data-qty], [data-remove], [data-gvb-add], [data-gvb-note], [data-gvb-gift], [data-gift-change], [data-to-fg], [data-box-edit]');
  const bag = t && t.closest('[data-gvb-bag]');
  if (!bag) return;
  if (t.matches('[data-gvb-note]')) { toggleNote(t); return; }
  if (t.matches('[data-gvb-add]')) { e.preventDefault(); add(t, bag); return; }
  if (t.matches('[data-gvb-gift]')) { e.preventDefault(); pickGift(t, bag); return; }
  if (t.matches('[data-gift-change]')) { e.preventDefault(); openGiftRow(t, bag); return; }
  if (t.matches('[data-to-fg]')) { e.preventDefault(); openGiftRow(t, bag); return; }
  if (t.matches('[data-box-edit]')) {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button > 0) return;
    e.preventDefault(); editBox(t, bag); return;
  }
  const line = t.closest('[data-gvb-line]');
  if (!line) return;
  e.preventDefault();
  if (line.getAttribute('aria-busy') === 'true' && t.matches('[data-remove]')) return;
  clearTimeout(line._gvbT);
  if (t.matches('[data-remove]')) { setQty(line, bag, 0); return; }
  const cur = line._gvbTarget != null ? line._gvbTarget : Number(line.dataset.k);
  let next = Math.max(0, cur + Number(t.dataset.qty));
  const max = Number(line.dataset.max || 0);
  if (max && next > max) next = max;
  if (next === cur) return;
  line._gvbTarget = next;
  const out = $('[data-qty-out]', line);
  if (out) out.textContent = next;
  /* several taps in a row send one request */
  line._gvbT = setTimeout(() => setQty(line, bag, line._gvbTarget), next === 0 ? 0 : 320);
});

/* before anyone re-renders the bag: remember note / open kit lists / scroll / focus; and make sure Impact's drawer
   gets its section (a cart:change without it throws inside Impact and leaves the drawer stale) */
window.addEventListener('cart:change', (e) => {
  const d = e.detail;
  if (!pending) hold();
  const drawer = document.getElementById('cart-drawer');
  if (!d || !d.cart || !drawer) return;
  const id = sectionIdOf(drawer);
  if (id && (!d.cart.sections || !d.cart.sections[id])) {
    d.cart.sections = Object.assign({}, d.cart.sections, { [id]: '<div>' + drawer.outerHTML + '</div>' });
    setTimeout(() => document.dispatchEvent(new CustomEvent('cart:refresh')), 0);
  }
}, true);
window.addEventListener('cart:refresh', () => { if (!pending) hold(); }, true);

/* the /cart page is not bundled by other add paths (PDP, Kaching, quick buy): refresh it from the section */
document.addEventListener('cart:change', (e) => {
  const d = e.detail || {};
  if (d.gvb || !pageBag()) return;
  const id = pageBag().dataset.sectionId;
  if (d.cart && d.cart.sections && d.cart.sections[id] && !pageView()) swapPage(d.cart.sections[id]); else refreshPage();
});
document.addEventListener('cart:refresh', () => { if (pageBag()) refreshPage(); });

watchAll();
sbScan(document);
document.addEventListener('shopify:section:load', () => { watchAll(); sbScan(document); });
/* the drawer's lazy thumbs load when it opens; classify anything still waiting then */
document.addEventListener('dialog:after-show', () => sbScan(document));
/* gvb-studio.js loads async: scan once more when it is there */
window.addEventListener('load', () => sbScan(document), { once: true });
